import { useEffect, useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRightIcon,
    ArrowTopRightOnSquareIcon,
    CheckIcon,
    ChevronRightIcon,
    SparklesIcon,
} from "@heroicons/react/24/outline";
import LandingNavbar from "../components/landing/LandingNavbar";
import Footer from "../components/landing/Footer";
import { marketingOrigin } from "../data/seo/siteEntity";
import { CASE_STUDIES, CASE_STUDIES_BY_SLUG } from "../data/caseStudies";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import { buildServiceSchema } from "../data/seo/structuredData";

const Eyebrow = ({ accent, children }: { accent: string; children: React.ReactNode }) => (
    <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
        <span className={`h-1.5 w-1.5 rounded-full bg-gradient-to-r ${accent}`} />
        {children}
    </div>
);

export default function CaseStudyPage() {
    const { slug } = useParams<{ slug: string }>();
    const study = slug ? CASE_STUDIES_BY_SLUG[slug] : undefined;

    const related = useMemo(
        () => (study ? CASE_STUDIES.filter((s) => s.slug !== study.slug) : []),
        [study],
    );

    usePageSeo({
        title: study?.seo.title,
        description: study?.seo.description,
        keywords: study?.seo.keywords.join(", "),
        canonical: study ? `/case-studies/${study.slug}` : undefined,
        ogType: "article",
    });

    const breadcrumbSchema = useMemo(() => {
        if (!study) return null;
        return {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${marketingOrigin()}/` },
                { "@type": "ListItem", position: 2, name: "Case Studies", item: `${marketingOrigin()}/case-studies` },
                {
                    "@type": "ListItem",
                    position: 3,
                    name: study.brand,
                    item: `${marketingOrigin()}/case-studies/${study.slug}`,
                },
            ],
        };
    }, [study]);

    const serviceSchema = useMemo(() => {
        if (!study) return null;
        return buildServiceSchema({
            name: `FloGPT for ${study.brand}`,
            description: study.summary,
            path: `/case-studies/${study.slug}`,
            serviceType: `Case study — ${study.useCase}`,
        });
    }, [study]);

    /**
     * Article + CaseStudy schema. Google + AI engines treat `Article` as a
     * first-class citation source, which is exactly what we want for case
     * studies that get linked from analyst write-ups and Perplexity answers.
     */
    const articleSchema = useMemo(() => {
        if (!study) return null;
        const url = `${marketingOrigin()}/case-studies/${study.slug}`;
        return {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: study.seo.title,
            description: study.seo.description,
            url,
            mainEntityOfPage: url,
            about: {
                "@type": "Organization",
                name: study.brand,
                url: study.url,
            },
            articleSection: "Case Study",
            keywords: study.seo.keywords.join(", "),
            inLanguage: "en",
        };
    }, [study]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        window.scrollTo({ top: 0, behavior: "auto" });
    }, [slug]);

    if (!study) {
        return <Navigate to="/case-studies" replace />;
    }

    const Icon = study.Icon;

    return (
        <div className="bg-slate-900 min-h-screen overflow-x-hidden text-slate-100">
            <LandingNavbar />

            <main className="pt-20">
                <section className="relative overflow-hidden">
                    <div className="pointer-events-none absolute inset-0 -z-10">
                        <div className={`absolute -left-32 top-10 h-72 w-72 rounded-full blur-3xl ${study.softAccent}`} />
                        <div className="absolute -right-32 bottom-10 h-72 w-72 rounded-full bg-pink-500/10 blur-3xl" />
                        <div className="absolute left-1/2 top-1/3 -translate-x-1/2 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
                    </div>

                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-12 lg:py-20">
                        <nav aria-label="Breadcrumb" className="text-xs text-slate-400 flex items-center gap-1.5 mb-6">
                            <Link to="/" className="hover:text-white transition-colors">
                                Home
                            </Link>
                            <ChevronRightIcon className="h-3 w-3" />
                            <Link to="/case-studies" className="hover:text-white transition-colors">
                                Case Studies
                            </Link>
                            <ChevronRightIcon className="h-3 w-3" />
                            <span className="text-slate-200">{study.brand}</span>
                        </nav>

                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                                className="lg:col-span-7"
                            >
                                <Eyebrow accent={study.accent}>Case study · {study.useCase}</Eyebrow>

                                <div className="mt-5 flex items-center gap-3">
                                    <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${study.accent} flex items-center justify-center shadow-lg`}>
                                        <Icon className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <div className="text-2xl font-bold text-white">{study.brand}</div>
                                        <div className="text-xs font-mono text-slate-500">{study.domain}</div>
                                    </div>
                                </div>

                                <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]">
                                    {study.tagline}
                                </h1>
                                <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-300 max-w-2xl">
                                    {study.summary}
                                </p>

                                <div className="mt-7 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-300">
                                    <span className="text-slate-500">Industry:</span>
                                    <span className="text-slate-200">{study.industry}</span>
                                </div>

                                <div className="mt-8 flex flex-wrap items-center gap-3">
                                    <a
                                        href={study.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`inline-flex items-center gap-2 rounded-xl bg-gradient-to-r ${study.accent} px-6 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5`}
                                    >
                                        Visit {study.domain}
                                        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                    </a>
                                    <Link
                                        to="/signup"
                                        className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                    >
                                        Start free
                                        <ArrowRightIcon className="h-4 w-4" />
                                    </Link>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 30, scale: 0.97 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ duration: 0.7, delay: 0.1 }}
                                className="lg:col-span-5"
                            >
                                <div className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6">
                                    <div className={`absolute -inset-2 rounded-3xl blur-2xl ${study.softAccent}`} />
                                    <div className="relative">
                                        <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4">
                                            At a glance
                                        </div>
                                        <dl className="space-y-4">
                                            {study.results.map((metric) => (
                                                <div key={metric.label}>
                                                    <dt className="text-[11px] uppercase tracking-wider text-slate-500">
                                                        {metric.label}
                                                    </dt>
                                                    <dd className={`mt-0.5 text-3xl font-bold bg-gradient-to-r ${study.accent} bg-clip-text text-transparent`}>
                                                        {metric.value}
                                                    </dd>
                                                    {metric.description && (
                                                        <dd className="mt-1 text-xs text-slate-400 leading-relaxed">
                                                            {metric.description}
                                                        </dd>
                                                    )}
                                                </div>
                                            ))}
                                        </dl>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                <section className="relative overflow-hidden">
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                viewport={{ once: true, amount: 0.3 }}
                                className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-8"
                            >
                                <Eyebrow accent={study.accent}>The challenge</Eyebrow>
                                <h2 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-white">
                                    Where the team was stuck
                                </h2>
                                <p className="mt-4 text-slate-300 leading-relaxed">{study.challenge}</p>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.05 }}
                                viewport={{ once: true, amount: 0.3 }}
                                className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-8"
                            >
                                <Eyebrow accent={study.accent}>The solution</Eyebrow>
                                <h2 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-white">
                                    What we shipped together
                                </h2>
                                <p className="mt-4 text-slate-300 leading-relaxed">{study.solution}</p>
                            </motion.div>
                        </div>
                    </div>
                </section>

                <section className="relative overflow-hidden">
                    <div className={`absolute inset-0 -z-10 ${study.softAccent} opacity-40`} />
                    <div className="absolute inset-0 -z-10 bg-slate-900/80" />
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true, amount: 0.3 }}
                            className="max-w-3xl"
                        >
                            <Eyebrow accent={study.accent}>FloGPT surfaces deployed</Eyebrow>
                            <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                                The exact stack {study.brand} runs
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed">
                                Click any surface to see the marketing page for that capability.
                            </p>
                        </motion.div>

                        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                            {study.deployed.map((feature, idx) => (
                                <motion.div
                                    key={feature.name}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: idx * 0.06 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                >
                                    <Link
                                        to={feature.href}
                                        className="group relative block rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-sm p-6 hover:border-white/20 hover:bg-slate-900/80 transition-all h-full"
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${study.accent} flex items-center justify-center shadow-lg shrink-0`}>
                                                <SparklesIcon className="h-5 w-5 text-white" />
                                            </div>
                                            <div className="min-w-0">
                                                <h3 className="text-lg font-semibold text-white group-hover:text-orange-400 transition-colors flex items-center gap-1.5">
                                                    {feature.name}
                                                    <ArrowRightIcon className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                </h3>
                                                <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                                                    {feature.description}
                                                </p>
                                            </div>
                                        </div>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="relative overflow-hidden">
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true, amount: 0.3 }}
                            className="max-w-3xl"
                        >
                            <Eyebrow accent={study.accent}>Highlights</Eyebrow>
                            <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                                What changed after launch
                            </h2>
                        </motion.div>

                        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
                            {study.highlights.map((highlight, idx) => (
                                <motion.div
                                    key={highlight}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: idx * 0.05 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${study.accent} flex items-center justify-center shrink-0`}>
                                            <CheckIcon className="h-4 w-4 text-white" />
                                        </div>
                                        <p className="text-slate-200 leading-relaxed">{highlight}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {study.pullQuote && (
                    <section className="relative overflow-hidden">
                        <div className="mx-auto max-w-5xl px-6 lg:px-8 py-16">
                            <motion.figure
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                viewport={{ once: true, amount: 0.3 }}
                                className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 p-10 sm:p-14 text-center overflow-hidden"
                            >
                                <div className={`absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full blur-3xl ${study.softAccent}`} />
                                <div className="relative">
                                    <blockquote className="text-2xl sm:text-3xl font-semibold text-white leading-snug max-w-3xl mx-auto">
                                        &ldquo;{study.pullQuote.quote}&rdquo;
                                    </blockquote>
                                    <figcaption className="mt-6 text-sm text-slate-400">
                                        — {study.pullQuote.attribution}
                                    </figcaption>
                                </div>
                            </motion.figure>
                        </div>
                    </section>
                )}

                <section className="relative overflow-hidden">
                    <div className="mx-auto max-w-5xl px-6 lg:px-8 py-20">
                        <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 p-10 sm:p-14 text-center overflow-hidden">
                            <div className={`absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full blur-3xl ${study.softAccent}`} />
                            <div className="relative">
                                <Eyebrow accent={study.accent}>Ready to ship?</Eyebrow>
                                <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                                    Build the same stack on your site
                                </h2>
                                <p className="mt-4 text-slate-300 leading-relaxed max-w-2xl mx-auto">
                                    The free plan ($0, 100 messages/month) ships the same chat widget, knowledge
                                    base, and unified CRM that powers the {study.brand} deployment above.
                                    Spin it up in minutes.
                                </p>
                                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                                    <Link
                                        to="/signup"
                                        className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
                                    >
                                        Start free
                                        <ArrowRightIcon className="h-4 w-4" />
                                    </Link>
                                    <Link
                                        to="/contact"
                                        className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                    >
                                        Book a 15-min demo
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {related.length > 0 && (
                    <section className="relative overflow-hidden">
                        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                viewport={{ once: true, amount: 0.3 }}
                                className="flex flex-wrap items-end justify-between gap-4 mb-10"
                            >
                                <div className="max-w-xl">
                                    <Eyebrow accent={study.accent}>Other case studies</Eyebrow>
                                    <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                                        Keep reading
                                    </h2>
                                </div>
                                <Link
                                    to="/case-studies"
                                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-400 hover:text-orange-300 transition-colors"
                                >
                                    All case studies
                                    <ArrowRightIcon className="h-4 w-4" />
                                </Link>
                            </motion.div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {related.map((rel, idx) => {
                                    const RelIcon = rel.Icon;
                                    return (
                                        <motion.div
                                            key={rel.slug}
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.4, delay: idx * 0.06 }}
                                            viewport={{ once: true, amount: 0.3 }}
                                        >
                                            <Link
                                                to={`/case-studies/${rel.slug}`}
                                                className="group relative block rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 hover:border-white/20 hover:bg-white/10 transition-all h-full"
                                            >
                                                <div className="flex items-start justify-between mb-3">
                                                    <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${rel.accent} flex items-center justify-center shadow-lg`}>
                                                        <RelIcon className="h-5 w-5 text-white" />
                                                    </div>
                                                    <span
                                                        className={`text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r ${rel.accent} bg-clip-text text-transparent`}
                                                    >
                                                        {rel.useCase}
                                                    </span>
                                                </div>
                                                <h3 className="text-lg font-semibold text-white group-hover:text-orange-400 transition-colors">
                                                    {rel.brand}
                                                </h3>
                                                <div className="mt-0.5 text-[11px] font-mono text-slate-500">{rel.domain}</div>
                                                <p className="mt-2 text-sm text-slate-400 leading-relaxed line-clamp-3">
                                                    {rel.tagline}
                                                </p>
                                                <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-orange-400">
                                                    Read
                                                    <ArrowRightIcon className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                                                </div>
                                            </Link>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                )}
            </main>

            <Footer />

            {breadcrumbSchema && <JsonLd id={`case-study-breadcrumb-${study.slug}`} data={breadcrumbSchema} />}
            {serviceSchema && <JsonLd id={`case-study-service-${study.slug}`} data={serviceSchema} />}
            {articleSchema && <JsonLd id={`case-study-article-${study.slug}`} data={articleSchema} />}
        </div>
    );
}
